import React from 'react';

// assets

// services
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';

// props

// components
import { ActionButton, Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Stack, TagPicker, TextField } from '@fluentui/react';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import { UnloadingLabourCostFormData } from '.';
import VendorsService from '../../../../../../services/data/vendors';
import VendorField from '../../../../../uiframeworks/forms/vendor';
import { IQuantityUnitResourceShortProps } from '../../../../../../props/general/quantityUnit';
import { PPhPercentageOptions, PPNPercentageOptions } from '../../../../../../manifests/taxPercentage';
import { IOutcomePricingProps } from '../../../../../../props/general/price';
import PricingService from '../../../../../../services/general/pricing';

type UnloadingLabourCostingFormProps = {
    quantityUnits: IQuantityUnitResourceShortProps[];
    data?: Partial<UnloadingLabourCostFormData>;
    onDismissed(): void;
    onSubmit(data: UnloadingLabourCostFormData): void;
}

const UnloadingLabourCostingForm: React.FC<UnloadingLabourCostingFormProps> = (props: UnloadingLabourCostingFormProps) => {
    const [error, setError] = React.useState<any>({});
    const [data, setData] = React.useState<Partial<UnloadingLabourCostFormData>>(props.data ? {
        ...props.data,
        price: Number(props.data?.price) + "",
        totalPrice: Number(props.data?.price) + ""
    } : {
        id: GeneralService.guid(),
        price: '',
        priceType: 'perquantity',
        quantity: '1',
        totalPrice: '',
        notes: ''
    });

    const isSubmitButtonDisabled = () => {
        if (!data.vendor || (data.vendor.verificationStatus !== 'verified' && data.vendor.verificationStatus !== 'verified_with_risk') || (data.price || "").trim() === "" || (data.quantity || "").trim() === "") {
            return true;
        } else if (error.notes || error.price || error.quantity) {
            return true;
        }

        return false;
    }

    const calculateTotalPrice = (_data: Partial<UnloadingLabourCostFormData>) => {
        let totalPrice = 0;

        if (_data.priceType === 'lumpsum') {
            totalPrice = Number(_data.price);
        } else if ((_data.price || "").trim() !== "" && (_data.quantity || "").trim() !== "") {
            totalPrice = Number(_data.price) * Number(_data.quantity);
        }

        return totalPrice;
    }
    
    const calculateAllPrice = (_data: Partial<UnloadingLabourCostFormData>) => {
        let pricing: IOutcomePricingProps = {
            price: _data.totalPrice || "0",
            ppn: _data.ppn || "0",
            pph: _data.pph || "0",
            includePPn: _data.includePPn || false,
            includePPh: _data.includePPh || false,
            ppnPercentage: _data.ppnPercentage || "0",
            pphPercentage: _data.pphPercentage || "0",
            finalPrice: _data.finalPrice || "0",
            nettPrice: _data.nettPrice || "0",
            invoicePrice: _data.invoicePrice || "0"
        }

        pricing = PricingService.outcome.calculate(pricing);
        setData({ ..._data, ...pricing, price: data.price });
    }

    return <Panel headerText={`${props.data ? 'Update' : 'Add'} Unloading Labour`}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={props.data ? "Update" : "Add"} disabled={isSubmitButtonDisabled()} onClick={() => props.onSubmit(data as UnloadingLabourCostFormData)} />
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            <VendorField required
                selected={data.vendor}
                allowCreate
                onChange={(vendor) => {
                    data.vendor = vendor;
                    setData({ ...data });
                }} />
            <Stack.Item>
                <TextField label={"Notes"}
                    value={data.notes}
                    multiline={true}
                    rows={1}
                    autoAdjustHeight
                    resizable={false}
                    onChange={(evt, value) => {
                        data.notes = value || "";

                        const validation = ValidationService.combination(value, ['limit'], { maxChars: 1000 });
                        error.notes = validation.message;

                        setData({ ...data });
                        setError({ ...error });
                    }}
                    errorMessage={error.notes} />
            </Stack.Item>
            <Stack className={'divider'}></Stack>
            <Stack.Item>
                <Dropdown label={"Price type"}
                    selectedKey={data.priceType}
                    options={[
                        { key: 'lumpsum', text: 'Lump sum' },
                        { key: 'perquantity', text: 'Per quantity' }
                    ]}
                    required={true}
                    onChange={(evt, opt) => {
                        data.priceType = opt ? opt.key as any : 'perquantity';
                        data.totalPrice = calculateTotalPrice(data).toFixed(2);

                        setData({ ...data });
                        calculateAllPrice(data);
                    }} />
            </Stack.Item>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <Stack.Item styles={{ root: { width: 100 } }}>
                        <TextField label={"Quantity"}
                            value={data.quantity}
                            required={true}
                            onChange={(evt, value) => {
                                if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                    data.quantity = value || "";
                                    data.totalPrice = calculateTotalPrice(data).toFixed(2);

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 20 });
                                    error.quantity = validation.message;

                                    setData({ ...data });
                                    calculateAllPrice(data);
                                    setError({ ...error });
                                }
                            }}
                            errorMessage={error.quantity} />
                    </Stack.Item>
                    <Stack.Item styles={{ root: { width: 100 } }}>
                        <Dropdown label={" "}
                            selectedKey={data.quantityUnit?.id}
                            options={props.quantityUnits.map((unit) => { return { key: unit.id, text: unit.name } })}
                            styles={{root: {marginTop: 3}}}
                            onChange={(evt, opt) => {
                                const selectedId = opt ? opt.key as string : undefined;
                                data.quantityUnit = props.quantityUnits.find((unit) => unit.id === selectedId);

                                setData({ ...data });
                            }} />
                    </Stack.Item>
                </Stack>
                <Stack.Item grow={1}>
                    <TextField label={data.priceType === 'perquantity' ? "Price per quantity" : "Price"}
                        value={data.price}
                        required={true}
                        prefix={'Rp'}
                        suffix={data.priceType === 'perquantity' ? '/ quantity' : undefined}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.price = value || "";
                                data.totalPrice = calculateTotalPrice(data).toFixed(2);

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 20 });
                                error.price = validation.message;

                                setData({ ...data });
                                calculateAllPrice(data);
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.price} />
                </Stack.Item>
            </Stack>
            <Stack className={'divider'}></Stack>
            {data.priceType === 'perquantity' ? <>
                <Stack.Item>
                    <TextField label={"Total price"}
                        value={GeneralService.getNumberWithSeparator(Number(data.totalPrice))}
                        disabled={true}
                        prefix={'Rp'}
                        errorMessage={error.quantity} />
                </Stack.Item>
            </> : null}
            <Stack tokens={{ childrenGap: 20 }} horizontal>
                <Stack.Item>
                    <Checkbox checked={data.includePPn}
                        label='Include PPn'
                        onChange={(ev, checked) => {
                            data.includePPn = checked;
                            setData({ ...data });
                            calculateAllPrice(data);
                        }} />
                </Stack.Item>
                <Stack.Item>
                    <Checkbox checked={data.includePPh}
                        label='Include PPh'
                        onChange={(ev, checked) => {
                            data.includePPh = checked;
                            setData({ ...data });
                            calculateAllPrice(data);
                        }} />
                </Stack.Item>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'}>PPn</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPNPercentageOptions}
                            selectedKey={data.ppnPercentage}
                            onChange={(evt, opt) => {
                                if (opt && opt.key !== 'custom') {
                                    data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                }
                                data.ppnPercentage = (opt?.key || "") as string;

                                setData({ ...data });
                                calculateAllPrice(data);
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField required={true}
                            value={data.ppn}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.ppn = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.ppn = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.ppn}
                            disabled={data.ppnPercentage !== 'custom'} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'}>PPh</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPhPercentageOptions}
                            selectedKey={data.pphPercentage}
                            onChange={(evt, opt) => {
                                if (opt && opt.key !== 'custom') {
                                    data.pph = (Number(opt.key) / 100 * Number(data.totalPrice)).toFixed();
                                }
                                data.pphPercentage = (opt?.key || "") as string;

                                setData({ ...data });
                                calculateAllPrice(data);
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField required={true}
                            value={data.pph}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.pph = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.pph = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.pph}
                            disabled={data.pphPercentage !== 'custom'} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack className={'divider'}></Stack>
            <Stack.Item>
                <TextField label={"Final price"}
                    value={GeneralService.getNumberWithSeparator(Number(data.finalPrice))}
                    disabled={true}
                    prefix={'Rp'}
                    errorMessage={error.quantity} />
            </Stack.Item>
        </Stack>
    </Panel>
};

export default UnloadingLabourCostingForm;
